import {
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button, Modal, Select, Tag } from "antd";
import { FC, MouseEvent, ReactNode } from "react";
import {
  TaskNodeDetailComment,
  TaskNodeDetailReply,
  TaskNodeDetailTask,
} from "../TaskNodeDetailModal";
import TaskCommentReplies from "../TaskCommentReplies";
import { TaskSubtaskItem } from "../TaskSubtaskManager";
import "./index.css";

interface TaskCommentModalProps {
  canComment: boolean;
  currentUserId: string;
  editingCommentId?: string;
  editor: ReactNode;
  formatTime: (timestamp: number) => string;
  getMemberName: (userId: string) => string;
  onCancelEdit: () => void;
  onClose: () => void;
  onDeleteComment: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment
  ) => void;
  onAddCommentReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    content: string
  ) => Promise<boolean>;
  onUpdateCommentReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    reply: TaskNodeDetailReply,
    content: string
  ) => Promise<boolean>;
  onDeleteCommentReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    reply: TaskNodeDetailReply
  ) => Promise<boolean>;
  onEditComment: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment
  ) => void;
  onSubmit: () => void;
  onSubtaskIdsChange: (subtaskIds: string[]) => void;
  onContentClick?: (event: MouseEvent<HTMLDivElement>) => void;
  open: boolean;
  submitting?: boolean;
  selectedSubtaskIds: string[];
  subtasks: TaskSubtaskItem[];
  task: TaskNodeDetailTask | null;
}

const TaskCommentModal: FC<TaskCommentModalProps> = ({
  canComment,
  currentUserId,
  editingCommentId,
  editor,
  formatTime,
  getMemberName,
  onCancelEdit,
  onClose,
  onDeleteComment,
  onAddCommentReply,
  onUpdateCommentReply,
  onDeleteCommentReply,
  onEditComment,
  onSubmit,
  onSubtaskIdsChange,
  onContentClick,
  open,
  submitting = false,
  selectedSubtaskIds,
  subtasks,
  task,
}) => {
  if (!task) {
    return null;
  }

  const comments = [...(task.comments || [])].sort(
    (prev, next) => next.created_at - prev.created_at
  );
  const assigneeNames = task.assignees.map(getMemberName);
  const subtaskTitleMap = new Map(
    subtasks.map((subtask) => [subtask.id, subtask.title])
  );

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      centered
      className="taskCommentWorkspace"
      closable={false}
      destroyOnClose
      footer={null}
      onCancel={onClose}
      open={open}
      title={null}
      width={1380}
    >
      <div className="taskCommentWorkspace__shell">
        <header className="taskCommentWorkspace__header">
          <div className="taskCommentWorkspace__identity">
            <span>节点批注</span>
            <h2>{task.title}</h2>
            <div className="taskCommentWorkspace__assignees">
              <small>负责人</small>
              {assigneeNames.length ? (
                assigneeNames.map((name, index) => (
                  <Tag key={`${task.assignees[index]}-${name}`} title={name}>
                    {name}
                  </Tag>
                ))
              ) : (
                <Tag>未设置</Tag>
              )}
            </div>
          </div>
          <div className="taskCommentWorkspace__summary">
            <div>
              <CommentOutlined />
              <strong>{comments.length}</strong>
              <span>条批注</span>
            </div>
            <Button
              aria-label="关闭批注弹窗"
              icon={<CloseOutlined />}
              onClick={onClose}
              title="关闭"
            />
          </div>
        </header>

        <div className="taskCommentWorkspace__body">
          <section className="taskCommentWorkspace__editorPane">
            <div className="taskCommentWorkspace__sectionTitle">
              <div>
                <span>WRITE</span>
                <h3>{editingCommentId ? "编辑批注" : "新增批注"}</h3>
              </div>
              {editingCommentId && <Tag>编辑中</Tag>}
            </div>
            {canComment ? (
              <div className="taskCommentWorkspace__editor">
                <div className="taskCommentWorkspace__relationField">
                  <label>关联子任务</label>
                  <Select
                    allowClear
                    aria-label="关联子任务"
                    maxTagCount="responsive"
                    mode="multiple"
                    onChange={onSubtaskIdsChange}
                    optionFilterProp="label"
                    options={subtasks.map((subtask) => ({
                      label: subtask.title,
                      value: subtask.id,
                    }))}
                    placeholder="不选择则关联整个任务模块"
                    showSearch
                    value={selectedSubtaskIds}
                  />
                  <small>
                    {selectedSubtaskIds.length
                      ? `已关联 ${selectedSubtaskIds.length} 个子任务`
                      : "当前批注关联整个任务模块"}
                  </small>
                </div>
                {editor}
                <p>支持文字与图片混排，可直接粘贴图片。</p>
                <div className="taskCommentWorkspace__editorActions">
                  {editingCommentId && (
                    <Button onClick={onCancelEdit}>取消编辑</Button>
                  )}
                  <Button
                    icon={<SendOutlined />}
                    loading={submitting}
                    onClick={onSubmit}
                    type="primary"
                  >
                    {editingCommentId ? "保存批注" : "发布批注"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="taskCommentWorkspace__empty">
                当前阶段为只读状态，无法新增批注。
              </div>
            )}
          </section>

          <section className="taskCommentWorkspace__listPane">
            <div className="taskCommentWorkspace__sectionTitle">
              <div>
                <span>HISTORY</span>
                <h3>全部批注</h3>
              </div>
              <Tag>{comments.length} 条</Tag>
            </div>
            <div className="taskCommentWorkspace__list">
              {comments.length ? (
                comments.map((comment) => {
                  const isOwnComment = comment.author === currentUserId;
                  return (
                    <article
                      className="taskCommentWorkspace__item"
                      key={comment.comment_id}
                    >
                      <div className="taskCommentWorkspace__itemHeader">
                        <div className="taskCommentWorkspace__author">
                          <span>{getMemberName(comment.author).slice(0, 1)}</span>
                          <div>
                            <strong>{getMemberName(comment.author)}</strong>
                            <small>
                              {formatTime(comment.updated_at || comment.created_at)}
                            </small>
                          </div>
                        </div>
                        {isOwnComment && canComment && (
                          <div className="taskCommentWorkspace__itemActions">
                            <Button
                              aria-label="编辑批注"
                              icon={<EditOutlined />}
                              onClick={() => onEditComment(task, comment)}
                              title="编辑批注"
                            />
                            <Button
                              aria-label="删除批注"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => onDeleteComment(task, comment)}
                              title="删除批注"
                            />
                          </div>
                        )}
                      </div>
                      <div
                        className="taskCommentWorkspace__content"
                        dangerouslySetInnerHTML={{ __html: comment.content }}
                        onClick={onContentClick}
                      />
                      <div className="taskCommentWorkspace__relations">
                        {(comment.subtask_ids || []).length ? (
                          comment.subtask_ids.map((subtaskId) => (
                            <Tag key={subtaskId}>
                              {subtaskTitleMap.get(subtaskId) || "已删除子任务"}
                            </Tag>
                          ))
                        ) : (
                          <Tag className="is-whole-task">整体任务模块</Tag>
                        )}
                      </div>
                      <TaskCommentReplies
                        canComment={canComment}
                        comment={comment}
                        currentUserId={currentUserId}
                        formatTime={formatTime}
                        getMemberName={getMemberName}
                        onAddReply={onAddCommentReply}
                        onDeleteReply={onDeleteCommentReply}
                        onUpdateReply={onUpdateCommentReply}
                        task={task}
                      />
                    </article>
                  );
                })
              ) : (
                <div className="taskCommentWorkspace__empty">
                  <CommentOutlined />
                  <strong>暂无批注</strong>
                  <span>发布第一条批注，开始围绕节点协作。</span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </Modal>
  );
};

export default TaskCommentModal;
